import React, { useState, useCallback, useEffect } from "react";
import {
  Palette,
  Eye,
  Maximize,
  ChevronDown,
  Grid,
  RotateCcw,
  CheckSquare,
  Square,
} from "lucide-react";

const PotreeControls = ({ viewer }) => {
  const [selectedColor, setSelectedColor] = useState("rgb(0, 0, 0)");
  const [isEDLEnabled, setIsEDLEnabled] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [opacity, setOpacity] = useState(1);
  const [pointSize, setPointSize] = useState(5);
  const [isGridVisible, setIsGridVisible] = useState(false);
  const [isBlinking, setIsBlinking] = useState(false);

  const backgroundColors = [
    { name: "Black", value: "rgb(0, 0, 0)" },
    { name: "White", value: "rgb(255, 255, 255)" },
  ];

  // Initialize viewer settings when component mounts
  useEffect(() => {
    if (viewer) {
      // Set initial EDL state
      viewer.setEDLEnabled(true);

      // Set initial background color
      viewer.setBackground({
        r: 0,
        g: 0,
        b: 0,
      });

      if (viewer.renderer) {
        viewer.renderer.setClearColor("rgb(0, 0, 0)");
        viewer.renderer.clear();
      }
    }
  }, [viewer]);

  // Start blinking when collapsed
  useEffect(() => {
    if (!isExpanded) {
      const interval = setInterval(() => {
        setIsBlinking(true);
        setTimeout(() => setIsBlinking(false), 200); // Close and reopen in 200ms
      }, 2000); // Blinks every 2 seconds
      return () => clearInterval(interval);
    }
  }, [isExpanded]);

  const updateRendererColor = useCallback(
    (colorValue) => {
      if (!viewer?.renderer) return;

      const rgbMatch = colorValue.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (!rgbMatch) return;

      // eslint-disable-next-line no-unused-vars
      const [fullMatch, r, g, b] = rgbMatch;

      // Update Potree background
      viewer.setBackground({
        r: parseInt(r) / 255,
        g: parseInt(g) / 255,
        b: parseInt(b) / 255,
      });

      // Update THREE.js renderer
      viewer.renderer.setClearColor(colorValue);
      viewer.renderer.clear();

      // Update THREE.js scene background if it exists
      if (viewer.scene?.scene?.background) {
        viewer.scene.scene.background.setStyle(colorValue);
      }

      // Force scene update
      if (viewer.scene) {
        viewer.scene.needUpdate = true;
      }

      // Request a new render frame
      requestAnimationFrame(() => {
        if (viewer.renderer && viewer.scene) {
          viewer.renderer.render(
            viewer.scene.scene,
            viewer.scene.getActiveCamera()
          );
        }
      });
    },
    [viewer]
  );

  const handleBackgroundChange = useCallback(
    (colorValue) => {
      if (!viewer) return;

      try {
        setSelectedColor(colorValue);
        updateRendererColor(colorValue);

        // Update materials after color change
        if (viewer.scene?.pointclouds) {
          viewer.scene.pointclouds.forEach((pointcloud) => {
            if (pointcloud.material) {
              pointcloud.material.needsUpdate = true;
            }
          });
        }
      } catch (error) {
        console.error("Error setting background color:", error);
      }
    },
    [viewer, updateRendererColor]
  );

  const handleToggleEDL = useCallback(() => {
    if (!viewer) return;

    try {
      const newEDLState = !isEDLEnabled;

      // Toggle EDL
      viewer.setEDLEnabled(newEDLState);

      // Update materials and ensure visibility
      if (viewer.scene?.pointclouds) {
        viewer.scene.pointclouds.forEach((pointcloud) => {
          if (pointcloud.material) {
            pointcloud.material.needsUpdate = true;
          }
        });
      }

      // Force color refresh
      updateRendererColor(selectedColor);

      setIsEDLEnabled(newEDLState);
    } catch (error) {
      console.error("Error toggling EDL:", error);
    }
  }, [viewer, isEDLEnabled, selectedColor, updateRendererColor]);

  const handleFitToScreen = useCallback(() => {
    if (viewer) {
      viewer.fitToScreen();
      // Refresh render after fit
      requestAnimationFrame(() => {
        if (viewer.renderer && viewer.scene) {
          viewer.renderer.render(
            viewer.scene.scene,
            viewer.scene.getActiveCamera()
          );
        }
      });
    }
  }, [viewer]);

  const handleOpacityChange = useCallback(
    (value) => {
      if (!value || isNaN(value)) return;
      value = Number(value);
      if (value < 0) value = 0;
      if (value > 1) value = 1;

      setOpacity(value);
      viewer.setEDLOpacity(value);
      /*viewer.scene.pointclouds.forEach((pointcloud) => {
      const baseSize = 1;  // Default point size
        const minSize = 0.1; // Minimum allowed point size
        const newSize = Math.max(minSize, baseSize * value); // Reduce size as opacity decreases

        pointcloud.material.size = newSize; // Apply new size
        pointcloud.material.needsUpdate = true;
    });*/
    },
    [viewer]
  );

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Point size change handler
  const handlePointSizeChange = (value) => setPointSize(value);

  // Toggle grid visibility
  const handleToggleGrid = () => setIsGridVisible(!isGridVisible);

  // Reset view function
  const handleResetView = () => {
    setSelectedColor("#ffffff");
    setOpacity(1);
    setPointSize(5);
    setIsGridVisible(false);
    setAllTabs(tabs.map((tab) => ({ ...tab, state: true }))); // Reset checkboxes
  };

  const tabs = [
    { name: "Actual Potree", id: "actual", type: "pCloud", state: true },
    { name: "Design Potree", id: "design", type: "pCloud", state: true },
    { name: "Under Break", id: "overBreak", type: "obj", state: true },
    { name: "Over Break", id: "underBreak", type: "obj", state: true },
  ];
  const [allTabs, setAllTabs] = useState(tabs);

  const handleCheckboxChange = (index) => {
    let pViewer = window.potreeViewer;
    if (!pViewer) return;

    const updatedTabs = [...allTabs];
    updatedTabs[index].state = !updatedTabs[index].state;
    let cItem = updatedTabs[index];

    switch (cItem.type) {
      case "pCloud":
        let pCloud = pViewer[cItem.id];
        if (pCloud) pCloud.visible = cItem.state;
        break;
      case "obj":
        let obj = pViewer.scene.scene.getObjectByName(cItem.id);
        if (obj) obj.visible = cItem.state;
        break;
      default:
        break;
    }

    setAllTabs(updatedTabs);
  };

  return (
    <div
      className={`absolute bottom-6 right-6 bg-white rounded-xl shadow-2xl p-4 z-50 border border-gray-200 transition-all duration-300 ${
        isExpanded ? "w-80" : "w-16 h-16 flex items-center justify-center p-2"
      }`}
    >
      {/* Toggle Button - Blinking Eye (Collapsed) or Title + Chevron (Expanded) */}
      <div className="cursor-pointer transition-all " onClick={toggleExpanded}>
        {isExpanded ? (
          <div className="flex items-center justify-between w-full p-2 hover:bg-gray-100">
            <span className="text-base font-semibold text-gray-800">
              Viewer Controls
            </span>
            <ChevronDown className="w-5 h-5 text-gray-600 transition-transform" />
          </div>
        ) : (
          <div className="relative flex items-center justify-center w-12 h-12 rounded-full shadow-lg border border-gray-300 bg-white">
            {/* SVG Eye with Blinking Animation */}
            <svg
              viewBox="0 0 64 64"
              className="w-10 h-10 transition-all duration-300"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Eyebrow */}
              <path
                d="M12 18 Q 32 2 52 18"
                stroke="black"
                strokeWidth="3"
                fill="transparent"
              />

              {/* Outer Eye Shape */}
              <path
                d="M12 32 Q 32 4 52 32 Q 32 60 12 32"
                stroke="black"
                strokeWidth="3"
                fill="transparent"
              />

              {/* Iris */}
              <circle
                cx="32"
                cy="32"
                r="10"
                stroke="black"
                strokeWidth="3"
                fill="white"
              />

              {/* Pupil */}
              <circle cx="32" cy="32" r="5" fill="black" />

              {/* Highlight */}
              <circle cx="34" cy="28" r="2" fill="white" />

              {/* Blinking Eyelid */}
              <path
                d="M12 32 Q 32 12 52 32 Q 32 52 12 32"
                fill="black"
                className={`transition-all duration-200 ${
                  isBlinking ? "opacity-100" : "opacity-0"
                }`}
              />
            </svg>
          </div>
        )}
      </div>

      {/* Collapsible Content */}
      <div
        className={`flex flex-col gap-4 mt-2 transition-all duration-300 overflow-hidden ${
          isExpanded
            ? "max-h-[500px] opacity-100"
            : "max-h-0 opacity-0 pointer-events-none"
        }`}
      >
        {/* Background Color Controls */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-800">
              Background
            </span>
          </div>
          <div className="flex gap-2">
            {backgroundColors.map((color) => (
              <button
                key={color.value}
                onClick={() => handleBackgroundChange(color.value)}
                className={`w-8 h-8 rounded-lg border-2 transition-transform ${
                  selectedColor === color.value
                    ? "border-blue-500 scale-110 shadow-md"
                    : "border-gray-300 hover:scale-105"
                }`}
                style={{ backgroundColor: color.value }}
              />
            ))}
          </div>
        </div>

        {/* Opacity Control */}
        <div className="space-y-2">
          <span className="text-sm font-medium text-gray-800">Opacity</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={opacity}
            onChange={(e) => handleOpacityChange(parseFloat(e.target.value))}
            className="w-full accent-blue-500"
          />
        </div>

        {/* Point Size Control */}
        <div className="space-y-2">
          <span className="text-sm font-medium text-gray-800">Point Size</span>
          <input
            type="range"
            min="1"
            max="10"
            step="0.1"
            value={pointSize}
            onChange={(e) => handlePointSizeChange(parseFloat(e.target.value))}
            className="w-full accent-blue-500"
          />
        </div>

        {/* Toggle Grid */}
        <button
          onClick={handleToggleGrid}
          className={`flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md transition ${
            isGridVisible
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-700"
          }`}
        >
          <Grid className="w-4 h-4" />
          Toggle Grid
        </button>

        {/* Visibility Controls */}
        <div className="space-y-2">
          <span className="text-sm font-medium text-gray-800">Visibility</span>
          <div className="grid grid-cols-2 gap-2">
            {allTabs.map((tab, index) => (
              <button
                key={tab.name}
                className={`flex items-center gap-2 p-2 rounded-md transition text-sm ${
                  tab.state
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-100 text-gray-700"
                }`}
                onClick={() => handleCheckboxChange(index)}
              >
                {tab.state ? (
                  <CheckSquare className="w-4 h-4" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        {/* View Controls */}
        <div className="flex justify-between gap-2">
          <button className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 rounded-md text-gray-700 transition hover:bg-gray-200">
            <Eye className="w-4 h-4" />
            EDL
          </button>

          <button className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 rounded-md text-gray-700 transition hover:bg-gray-200">
            <Maximize className="w-4 h-4" />
            Fit
          </button>

          <button
            onClick={handleResetView}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-red-100 rounded-md text-red-700 transition hover:bg-red-200"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default PotreeControls;
